import {isEqual} from 'lodash';
import {Mutable} from 'utility-types';

import {ContactModel} from '../../../models/api/contactModel';
import {AppAction} from '../../appActions';
import {ResourceMap} from '../../appTypes';
import {DataGroupsActionsEnum} from '../dataGroupsActions';

/*
 * Contacts reducer.
 */

export function contactsReducer(
  state: ResourceMap<ContactModel>,
  action: AppAction,
): ResourceMap<ContactModel> {
  switch (action.type) {
    // Data Group.
    case DataGroupsActionsEnum.DATA_UPDATED: {
      const {contacts} = action.payload.result;
      if (!contacts || contacts.length === 0) {
        return state;
      }

      // Either the new state, or undefined if there are no state changes.
      let newState: Mutable<ResourceMap<ContactModel>> | undefined;

      // Optimization: iterate the updates once instead of _().groupBy/keyBy/sortBy/maxBy.
      contacts.forEach((updatedContact) => {
        const existingContact = (newState || state)[updatedContact.uid];

        const newContact = mergeContact(existingContact, updatedContact);
        if (newContact === existingContact) {
          return;
        }

        // Optimization: create a copy of the state once on the first state change.
        newState = newState || {...state};
        newState[updatedContact.uid] = newContact;
      });

      return newState || state;
    }

    default:
      return state;
  }
}

/*
 * Contact merger.
 */

function mergeContact(existingContact: ContactModel | undefined, contact: ContactModel): ContactModel {
  // If no existing contact is found, nothing to merge.
  if (!existingContact) {
    return contact;
  }

  if (isEqual(existingContact, contact)) {
    return existingContact;
  }

  // Otherwise, merge the card IDs.
  return {
    ...contact,
    cardIds: {
      ...existingContact.cardIds,
      ...contact.cardIds,
    },
    // TEMPORARY: Merge optional properties until they're returned by the Calendar Participants.
    url: existingContact.url || contact.url,
    channelId: existingContact.channelId || contact.channelId,
    avatarUrl: existingContact.avatarUrl || contact.avatarUrl,
    fallbackContactUid: existingContact.fallbackContactUid || contact.fallbackContactUid,
  };
}
